import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { eventsAPI } from '../api';
import RecommendedSection from '../components/RecommendedSection';
import { Calendar, MapPin, Clock, ArrowLeft, ExternalLink, Share2 } from 'lucide-react';
import DOMPurify from 'dompurify';

const fmtFull = (iso) =>
  new Date(iso).toLocaleString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

const EventDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setUpcoming([]);
    eventsAPI
      .bySlug(slug)
      .then((r) => setEv(r.data))
      .catch(() => setError('not-found'))
      .finally(() => setLoading(false));
    // Right-hand "coming soon" rail — 6 next events, excluding this one so
    // the visitor never sees the event they're already looking at.
    eventsAPI
      .upcoming(6, slug)
      .then((r) => setUpcoming(r.data || []))
      .catch(() => setUpcoming([]));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (error || !ev) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Event not found</h1>
          <p className="text-gray-600 mb-6">This event may have been removed or the link is incorrect.</p>
          <button onClick={() => navigate('/events')} className="text-cyan-600 font-medium">
            ← Back to all events
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Build JSON-LD for Schema.org Event — structured for Google Events rich
  // results: PostalAddress location, absolute image URLs, endDate and offers.
  const absImage = ev.image_url
    ? (ev.image_url.startsWith('http') ? ev.image_url : `https://paisley.org.uk${ev.image_url}`)
    : 'https://media.paisley.org.uk/site/og-paisley-abbey.jpg';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: ev.title,
    startDate: ev.start_date,
    endDate: ev.end_date || ev.start_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: ev.venue || 'Paisley',
      address: {
        '@type': 'PostalAddress',
        streetAddress: ev.address || ev.venue || 'Paisley Town Centre',
        addressLocality: 'Paisley',
        addressRegion: 'Renfrewshire',
        addressCountry: 'GB',
      },
    },
    image: [absImage],
    description: ev.description || '',
    organizer: { '@type': 'Organization', name: 'Paisley.org.uk', url: 'https://paisley.org.uk' },
    url: `https://paisley.org.uk/events/${ev.slug}`,
    ...(ev.link
      ? {
          offers: {
            '@type': 'Offer',
            url: ev.link,
            availability: ev.sold_out ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
            validFrom: ev.created_at || ev.start_date,
          },
        }
      : {}),
  };

  const safeDesc = DOMPurify.sanitize(
    (ev.description || '').split(/\n\n+/).map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`).join(''),
    { ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li'], ALLOWED_ATTR: ['href', 'target', 'rel'] },
  );

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`${ev.title} | Paisley Events`}
        description={(ev.description || `Event at ${ev.venue || 'Paisley'}.`).slice(0, 160)}
        keywords={`Paisley event, ${ev.venue}, ${ev.category}, Paisley what's on, ${ev.title}`}
        image={ev.image_url}
        url={`/events/${ev.slug}`}
        type="article"
        emitArticleSchema={false}
      />
      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />

      {/* Hero with image */}
      <section className="relative bg-slate-900">
        {ev.image_url && (
          <div className="absolute inset-0 opacity-30">
            <img src={ev.image_url} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-slate-900/30" />
          </div>
        )}
        <div className="relative max-w-4xl mx-auto px-6 py-16">
          <Link to="/events" className="inline-flex items-center gap-1 text-cyan-300 text-sm mb-6 hover:text-cyan-200">
            <ArrowLeft size={14} /> Back to all events
          </Link>
          {(ev.featured || ev.sold_out) && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {ev.featured && (
                <span className="inline-block text-xs uppercase font-bold tracking-wider text-fuchsia-300 bg-fuchsia-500/20 backdrop-blur px-3 py-1 rounded-full">★ Featured event</span>
              )}
              {ev.sold_out && (
                <span
                  className="inline-block text-xs uppercase font-bold tracking-wider text-rose-200 bg-rose-500/25 border border-rose-400/40 backdrop-blur px-3 py-1 rounded-full"
                  data-testid="event-detail-sold-out"
                >
                  Sold out
                </span>
              )}
            </div>
          )}
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight" data-testid="event-detail-title">
            {ev.title}
          </h1>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-200">
            <span className="flex items-center gap-2"><Calendar size={16} className="text-cyan-300" />{fmtFull(ev.start_date)}</span>
            {ev.end_date && <span className="flex items-center gap-2 text-gray-400 text-sm">→ {fmtFull(ev.end_date)}</span>}
          </div>
          {ev.venue && (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-gray-200 mt-2">
              <span className="flex items-center gap-2"><MapPin size={16} className="text-cyan-300" />{ev.venue}{ev.address ? `, ${ev.address}` : ''}</span>
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1fr_320px] gap-10">
          <article className="prose prose-slate max-w-none" data-testid="event-detail-body">
            <img
              src={ev.image_url || '/event-placeholder.jpg'}
              alt={ev.title}
              className="w-full rounded-xl mb-8"
              onError={(e) => {
                if (!e.target.dataset.f) {
                  e.target.dataset.f = '1';
                  e.target.src = '/event-placeholder.jpg';
                }
              }}
            />
            <div dangerouslySetInnerHTML={{ __html: safeDesc }} />
          </article>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="bg-slate-50 rounded-xl p-5 border border-gray-200" data-testid="event-details-card">
              <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Event details</p>
              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-gray-500 flex items-center gap-1.5"><Clock size={12} />Starts</dt>
                  <dd className="text-slate-900 font-medium">{fmtFull(ev.start_date)}</dd>
                </div>
                {ev.end_date && (
                  <div>
                    <dt className="text-gray-500 flex items-center gap-1.5"><Clock size={12} />Ends</dt>
                    <dd className="text-slate-900 font-medium">{fmtFull(ev.end_date)}</dd>
                  </div>
                )}
                {ev.venue && (
                  <div>
                    <dt className="text-gray-500 flex items-center gap-1.5"><MapPin size={12} />Venue</dt>
                    <dd className="text-slate-900 font-medium">{ev.venue}</dd>
                    {ev.address && <dd className="text-xs text-gray-500">{ev.address}</dd>}
                  </div>
                )}
                <div>
                  <dt className="text-gray-500">Category</dt>
                  <dd className="text-slate-900 font-medium capitalize">{ev.category}</dd>
                </div>
                {ev.sold_out && (
                  <div>
                    <dt className="text-gray-500">Availability</dt>
                    <dd className="text-rose-700 font-semibold" data-testid="event-sidebar-sold-out">
                      Sold out at the box office
                    </dd>
                  </div>
                )}
              </dl>
              {ev.link ? (
                <a
                  href={ev.link}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  className={`mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
                    ev.sold_out
                      ? 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-gray-200'
                      : 'bg-cyan-500 hover:bg-cyan-600 text-white'
                  }`}
                  data-testid="event-detail-cta"
                >
                  {ev.sold_out ? 'Sold out — check for returns' : 'Find tickets'} <ExternalLink size={14} />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    const url = window.location.href;
                    if (navigator.share) {
                      navigator.share({ title: ev.title, url }).catch(() => {});
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(url);
                    }
                  }}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                  data-testid="event-detail-cta"
                >
                  <Share2 size={14} /> Share this event
                </button>
              )}
              {ev.link && (
                <button
                  type="button"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: ev.title, url: window.location.href }).catch(() => {});
                    } else if (navigator.clipboard) {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-100 text-slate-700 border border-gray-200 px-4 py-2.5 rounded-lg font-medium transition-colors"
                >
                  <Share2 size={14} /> Share
                </button>
              )}
              {ev.source && (
                <p className="text-[10px] text-slate-400 mt-3 uppercase tracking-wider text-center">
                  Tickets via {ev.source}
                </p>
              )}
            </div>

            {/* Coming soon rail — pulls from /events/upcoming so anything
                published via Ticketmaster/Skiddle sync shows up automatically. */}
            {upcoming.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="event-upcoming-rail">
                <div className="px-5 py-4 bg-slate-900 text-white">
                  <p className="text-xs uppercase tracking-wider font-semibold text-cyan-300 mb-0.5">Coming up</p>
                  <p className="text-sm font-bold">More events soon</p>
                </div>
                <ul className="divide-y divide-gray-100">
                  {upcoming.map((u) => {
                    const d = new Date(u.start_date);
                    const day = d.toLocaleDateString('en-GB', { day: '2-digit' });
                    const mon = d.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase();
                    return (
                      <li key={u.id}>
                        <Link
                          to={`/events/${u.slug}`}
                          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                          data-testid={`upcoming-${u.slug}`}
                        >
                          <div className="shrink-0 w-12 text-center bg-slate-100 rounded-md py-1">
                            <div className="text-[10px] font-bold text-slate-500">{mon}</div>
                            <div className="text-lg font-bold text-slate-900 leading-none">{day}</div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 line-clamp-2 leading-tight">
                              {u.title}
                            </p>
                            {u.venue && (
                              <p className="text-[11px] text-slate-500 truncate">{u.venue}</p>
                            )}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
                <Link
                  to="/events"
                  className="block text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-cyan-600 hover:bg-slate-50 border-t border-gray-100"
                >
                  All upcoming events →
                </Link>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Single affiliate slot at the bottom — hides silently if the admin
          has no products with an image (per Brian: no image = no link). */}
      <RecommendedSection
        single
        requireImage
        eyebrow="Enjoying this event?"
      />

      <Footer />
    </div>
  );
};

export default EventDetail;
